import type { SchedulerDepartment } from 'core/API/scheduler';
import {
	DepartmentFilterSelection,
	isAllDepartmentsSelected,
} from './scheduleResourceUtils';

export const SCHEDULE_DEPARTMENT_FILTER_STORAGE_PREFIX = 'schedule-department-filter';

export const getScheduleDepartmentFilterStorageKey = (
	workplaceId: number | string,
	userId?: number | string
): string =>
	userId != null && String(userId) !== ''
		? `${SCHEDULE_DEPARTMENT_FILTER_STORAGE_PREFIX}:${userId}:${workplaceId}`
		: `${SCHEDULE_DEPARTMENT_FILTER_STORAGE_PREFIX}:${workplaceId}`;

const parseStoredSelection = (raw: string | null): DepartmentFilterSelection | null => {
	if (!raw) return null;

	try {
		const parsed = JSON.parse(raw);
		if (parsed === 'all') return 'all';
		if (Array.isArray(parsed) && parsed.every((id) => Number.isFinite(Number(id)))) {
			return parsed.map(Number);
		}
		return null;
	} catch {
		return null;
	}
};

export const getPersistedDepartmentFilter = (
	workplaceId: number | string,
	userId?: number | string
): DepartmentFilterSelection | null => {
	try {
		return parseStoredSelection(
			localStorage.getItem(getScheduleDepartmentFilterStorageKey(workplaceId, userId))
		);
	} catch {
		return null;
	}
};

export const persistDepartmentFilter = (
	workplaceId: number | string,
	selection: DepartmentFilterSelection,
	departments: SchedulerDepartment[],
	userId?: number | string
): void => {
	try {
		const toStore = isAllDepartmentsSelected(selection, departments) ? 'all' : selection;
		localStorage.setItem(
			getScheduleDepartmentFilterStorageKey(workplaceId, userId),
			JSON.stringify(toStore)
		);
	} catch {
		// Ignore storage quota / privacy mode errors.
	}
};

export const resolveDepartmentFilterSelection = (
	stored: DepartmentFilterSelection | null,
	departments: SchedulerDepartment[]
): DepartmentFilterSelection => {
	if (departments.length === 0) return 'all';
	if (stored === null || stored === 'all') return 'all';
	if (!Array.isArray(stored)) return 'all';
	if (stored.length === 0) return [];

	const validIds = new Set(departments.map((department) => department.id));
	const next = stored.filter((id) => validIds.has(id));

	if (next.length === 0) return 'all';
	if (isAllDepartmentsSelected(next, departments)) return 'all';
	return next;
};

export const clearScheduleDepartmentFilter = (): void => {
	try {
		Object.keys(localStorage)
			.filter((key) => key.startsWith(SCHEDULE_DEPARTMENT_FILTER_STORAGE_PREFIX))
			.forEach((key) => localStorage.removeItem(key));
	} catch {
		// Ignore storage access errors.
	}
};
